import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, BookOpen, ChevronRight, Star, FileText, GraduationCap } from 'lucide-react';
import PageLoader from '../../../components/PageLoader';
import { getAdminStats } from '../../../api';

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalCourses: 0,
    totalBrokers: 0,
    totalFaqs: 0,
    totalBasicQuizQuestions: 0,
    totalReviews: 0,
    totalEnrollments: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await getAdminStats();
        if (!mounted || !data) return;
        setStats((prev) => ({ ...prev, ...data }));
        setError('');
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to load admin stats');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const metrics = [
    {
      label: 'learners',
      value: stats.totalUsers,
      sub: 'Registered accounts',
      icon: Users,
      to: '/admin/users',
      gradient: 'from-primary-500 to-primary-700',
      bg: 'bg-primary-500/10',
      border: 'border-primary-200',
    },
    {
      label: 'Share market courses',
      value: stats.totalCourses,
      sub: 'Live courses',
      icon: BookOpen,
      to: '/admin/courses',
      gradient: 'from-emerald-500 to-emerald-700',
      bg: 'bg-emerald-500/10',
      border: 'border-emerald-200',
    },
    {
      label: 'Total enrollments',
      value: stats.totalEnrollments,
      sub: 'Course sign-ups',
      icon: GraduationCap,
      to: '/admin/courses',
      gradient: 'from-blue-500 to-blue-700',
      bg: 'bg-blue-500/10',
      border: 'border-blue-200',
    },
    {
      label: 'Course reviews',
      value: stats.totalReviews,
      sub: 'Ratings & feedback',
      icon: Star,
      to: '/admin/reviews',
      gradient: 'from-amber-500 to-amber-600',
      bg: 'bg-amber-500/10',
      border: 'border-amber-200',
    },
  ];

  const actions = [
    { to: '/admin/courses', label: 'Manage courses', desc: 'Lessons & course quiz', icon: BookOpen },
    { to: '/admin/reviews', label: 'Reviews & ratings', desc: 'Moderate feedback', icon: Star },
    { to: '/admin/cms', label: 'CMS & content', desc: 'Basic quiz, FAQ, pages', icon: FileText },
  ];

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Header card */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-7 flex flex-col gap-3">
        <div className="flex items-center gap-2 text-xs font-semibold text-primary-700 uppercase tracking-wider">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-primary-50 border border-primary-100">
            Admin Dashboard
          </span>
          <span className="hidden sm:inline text-gray-400">•</span>
          <span className="hidden sm:inline text-gray-500">Share market control panel</span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
          Overview of your trading academy
        </h1>
        <p className="text-sm text-gray-600 max-w-2xl">
          Quickly see how many traders, courses and enrollments you have. Use the cards below to dive
          into users, courses, reviews and CMS.
        </p>
      </div>

      {/* Metrics grid */} 
      <div>
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Key metrics</h2>
        {error && (
          <p className="text-xs text-danger-600 mb-2">{error}</p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {metrics.map(({ label, value, sub, icon: Icon, to, gradient }) => (
            <Link
              key={to + label}
              to={to}
              className="group bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md hover:border-primary-200 transition-all p-4 flex flex-col gap-3"
            >
              <div className="flex items-center justify-between">
                <div
                  className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center text-white shadow-sm`}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary-600 group-hover:translate-x-0.5 transition-all" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 leading-tight">{value}</p>
                <p className="text-sm font-medium text-gray-800 mt-0.5">{label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{sub}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Quick actions */} 
      <div>
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Quick actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {actions.map(({ to, label, desc, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              className="group bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md hover:border-primary-200 transition-all p-4 flex flex-col gap-3"
            >
              <div className="w-10 h-10 rounded-xl bg-gray-100 group-hover:bg-primary-50 flex items-center justify-center text-gray-600 group-hover:text-primary-600 transition-colors">
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 group-hover:text-primary-800">{label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
